import { useCallback, useState } from 'react'
import './CancellationPopUp.scss'
import { ConfirmationPopup } from '../../../../components/confirmation-popup/confirmationpopup'
import Portal from '../../../../components/portal/Portal'
import { CharacterLimitCounter, Dropdown, ErrorAlert, PageLoadingSpinner, TextEditor } from 'ui_kit_qmd'
import cautionIcon from '../../../../images/common/caution.png'
import errorIcon from '../../../../images/common/error-icon.svg'
import { text } from './constants'
import { useDispatch, useSelector } from 'react-redux'

import { type LookupsState } from '../../../../store/lookups/types'
import { apiCall } from '../../../../utils/restclient'
import { methods } from '../../../../utils/apiHelper'
import { getPatientPrescriptions } from '../../../../store/charts/ChartsThunk'
import { getValue } from '@quickmd-llc/frontend-utils'
import { useQueryClient } from '@tanstack/react-query'
import { getGetPatientsPatientIdPrescriptionsQueryKey } from '../../../../api/generated/prescription/prescription'

interface Props {
  onClosePopUp: () => void
  orderId: string
  patientId: string
}
const MAX_COMMENT_LENGTH = 100
export const CancellationPopUp = ({ onClosePopUp, orderId, patientId }: Props) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const reasons = useSelector((state: { lookups: LookupsState }) => state.lookups.orderCancelReasons || [])
  const [reason, setReason] = useState('')
  const [additionalInfo, setAdditionalInfo] = useState('')
  const [dropdownError, setDropdownError] = useState('')
  const [textError, setTextError] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const showError = useCallback((msg: string) => {
    setError(msg)
    setTimeout(() => setError(''), 3500)
  }, [])
  const handleReasonChange = useCallback((value: string) => {
    setReason(value)
    setDropdownError('')
  }, [])
  const handleTextChange = useCallback((value: string) => {
    setAdditionalInfo(value)
    setTextError('')
  }, [])
  const handleCancelOrder = useCallback(() => {
    const selectedReason = reasons.find((item) => item.Text === reason)
    if (!selectedReason) {
      showError('Please select a reason')
      setDropdownError('A reason code is required')
      return
    }
    if (!additionalInfo.length) {
      const errorMsg = 'Additional information is required'
      showError(errorMsg)
      setTextError(errorMsg)
      return
    }
    if (additionalInfo.length > MAX_COMMENT_LENGTH) {
      const errorMsg = 'Character limit exceeded.'
      showError(errorMsg)
      setTextError(errorMsg)
      return
    }
    setLoading(true)
    apiCall(
      `api/v1/orders/cancel`,
      methods.DELETE,
      null,

      // @ts-expect-error TS2345 TEMP ERROR SUPPRESSION: CRA -> REACT ROUTER
      JSON.stringify({
        OrderId: orderId,
        PatientId: patientId,
        ReasonId: selectedReason.Id,
        Comment: additionalInfo,
      }),
    )
      .then(() => {
        // @ts-expect-error TS2345 TEMP ERROR SUPPRESSION: CRA -> REACT ROUTER
        dispatch(getPatientPrescriptions(patientId))
        void queryClient.invalidateQueries({ queryKey: getGetPatientsPatientIdPrescriptionsQueryKey(patientId) })
        onClosePopUp()
      })
      .catch((e) => {
        const message =
          getValue(e, 'ErrorCode') === 'CancellationNotAllowed' ? 'Cancellation of this order is not allowed'
          : getValue(e, 'ErrorCode') === 'OrderNotFound' ? 'Order was not found'
          : 'Internal server error'
        showError(message)
      })
      .finally(() => setLoading(false))
  }, [reason, additionalInfo, reasons, orderId, patientId, onClosePopUp, showError, dispatch, queryClient])
  return (
    <Portal>
      {loading ?
        <PageLoadingSpinner />
      : null}
      {error ?
        <ErrorAlert
          styleContainer={{ width: 550, height: 56, background: '#FDF5F2' }}
          errorText={error}
          errorImg={errorIcon}
          imgAlt='Error'
        />
      : null}
      <ConfirmationPopup
        title={text.CONFIRM_CANCELLATION}
        titleIcon={cautionIcon}
        className='cancellation-pop-up-container'
        id='admin-availability-calendar-delete-pop-up'
        primaryBtnText={text.CONFIRM_CANCELLATION}
        primaryBtnAction={handleCancelOrder}
        secondaryBtnText='Go back'
        secondaryBtnAction={onClosePopUp}
        closeAction={undefined}
        isCaution
        msg={
          <div className='cancellation-pop-up-message-container'>
            <div className='cancellation-pop-up-message'>{text.CANCELLATION_MESSAGE}</div>
            <Dropdown
              options={reasons.map((item) => item.Text)}
              value={reason}
              setValue={handleReasonChange}
              label='Select reason code*'
              containerStyle={{ width: '100%' }}
              localError={dropdownError}
            />
            <div>
              <TextEditor
                placeHolderText={text.ADDITIONAL_INFORMATION}
                id='cancellationPopUpTextEditor'
                setText={handleTextChange}
                descriptionLocalError={textError}
                isError={textError}
              />
              <CharacterLimitCounter
                currentAmount={additionalInfo.length}
                limit={MAX_COMMENT_LENGTH}
                isHighlightError={additionalInfo.length > MAX_COMMENT_LENGTH}
              />
            </div>
            <div className='cancellation-pop-up-question'>{text.CANCELLATION_QUESTION}</div>
          </div>
        }
        popUpBoxStyle={undefined}
        extraView={undefined}
        buttonWidth={undefined}
      />
    </Portal>
  )
}
